import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Button, Dropdown, Tooltip} from 'antd';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {EllipsisOutlined} from '@ant-design/icons';
import {StyledExpenseDetailsTabFooter, StyledExpenseDetailsTabFooterSpace} from './StyledExpenseDetailsTabFooter';
import IconWrapper from '../../../../IconWrapper';
import {CheckIcon, CloseIcon, TrashIcon} from '../../../../../icons';
import {expensesHelpers, reviewHelpers } from '../../../../../utils/helpers';
import {colorsStyles} from '../../../../../utils/styles';

const menuItemStyle = {
  border: `1px solid ${colorsStyles.neutral_3}`,
  marginBottom: 8,
  fontSize: 14,
  padding: '12px',
  width: 120,
  height: 34,
};

const ExpenseDetailsTabFooter = ({
  expense,
  onApprove,
  onDelete,
  onReject,
  onSubmit,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [submitBtnProps, setSubmitBtnProps] = React.useState({loading: false});

  const { isAdmin, employeeEmail } = useSelector(({ user }) => ({
    employeeEmail: user?.employee?.email,
    isAdmin: user.isAdmin,
  }));

  const isDraftExpense = expensesHelpers.isExpenseDraft(expense);

  const isSubmitVisible = useMemo(() => {
    return expensesHelpers.isExpenseSubmitAvailable({employeeEmail, expense, isAdmin});
  }, [employeeEmail, expense?.review_status, expense?.reported_by?.email, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDisabledSubmit = useMemo(() => {
    const isDraftExpense = expensesHelpers.isExpenseDraft(expense);
    if (!isDraftExpense) return true;

    return expensesHelpers.hasExpenseMissingRequiredFields(expense);
  }, [expense]);

  const isDeleteVisible = useMemo(() => {
    const isDraftExpense = expensesHelpers.isExpenseDraft(expense);
    return isDraftExpense && !expense?.card_transaction_id;
  }, [isSubmitVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const isApprovalAvailable = useMemo(() => {
    return expensesHelpers.isAvailableApproveActions({employeeEmail, expense, isAdmin});
  }, [isAdmin, employeeEmail, expense?.approval, expense?.review_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReviewAction = (e, action) => {
    const override = isAdmin && !reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);
    action?.({e, expense, data: { admin_override: override }});
  };

  const handleSubmitExpense = (e) => {
    if (!isSubmitVisible || isDisabledSubmit || !onSubmit) return;

    setSubmitBtnProps({loading: true});
    onSubmit({
      e,
      expense,
      callbackFunc: () => setSubmitBtnProps({ loading: false }),
    });
  }

  const handleApprove = (e) => handleReviewAction(e, onApprove);

  const handleReject = (e) => handleReviewAction(e, onReject);

  const handleDelete = (e) => onDelete?.({e, expense});

  const renderIcon = (icon, color = colorsStyles.neutral_10) => (
    <IconWrapper size={21} color={color}>{icon}</IconWrapper>
  );

  const handleMenuClick = ({key, domEvent}) => {
    ({
      approve: () => handleApprove(domEvent),
      reject: () => handleReject(domEvent),
      delete: () => handleDelete(domEvent),
    }[key] || (() => {}))();
  }

  const menuProps = {
    onClick: handleMenuClick,
    items: [
      {
        label: t('approve'),
        key: 'approve',
        icon: renderIcon(<CheckIcon />),
        style: menuItemStyle,
      },
      {
        label: t('reject'),
        key: 'reject',
        icon: renderIcon(<CloseIcon />),
        style: menuItemStyle,
      },
      isDeleteVisible && {
        label: t('delete'),
        key: 'delete',
        icon: renderIcon(<TrashIcon />, colorsStyles.functional_danger),
        style: {
          ...menuItemStyle,
          borderBottom: `1px solid ${colorsStyles.neutral_3}`,
          marginBottom: 0
        },
      },
    ].filter(Boolean),
  };

  const getActionsMenu = () => {
    if (!isDraftExpense) return null;

    if (isAdmin) {
      return (
        <Dropdown menu={menuProps} placement='bottomRight'>
          <Button size='large' icon={<EllipsisOutlined />} />
        </Dropdown>
      );
    }

    if (isDeleteVisible) {
      return (
        <Tooltip title={t('delete')}>
          <Button className='expense-delete-btn' size='large' icon={<TrashIcon/>} onClick={handleDelete} />
        </Tooltip>
      );
    }

    return null;
  }

  if (isSubmitVisible) {
    const actionsMenu = getActionsMenu();
    return (
      <StyledExpenseDetailsTabFooter {...rest}>
        <StyledExpenseDetailsTabFooterSpace>
          <Button
            disabled={isDisabledSubmit}
            onClick={handleSubmitExpense}
            size='large'
            type='primary'
            {...submitBtnProps}
          >
            {t('File')} {t('expense')}
          </Button>
          {actionsMenu}
        </StyledExpenseDetailsTabFooterSpace>

      </StyledExpenseDetailsTabFooter>
    );
  }

  if (isApprovalAvailable) {
    return (
      <StyledExpenseDetailsTabFooter {...rest}>
        <Button
          icon={<CloseIcon />}
          onClick={handleReject}
          size='large'
        >
          {t('reject')}
        </Button>
        <Button
          icon={<CheckIcon />}
          onClick={handleApprove}
          size='large'
          type='primary'
        >
          {t('approve')}
        </Button>
      </StyledExpenseDetailsTabFooter>
    );
  }

  return null;
}

ExpenseDetailsTabFooter.propTypes = {
  expense: PropTypes.object,
  onApprove: PropTypes.func,
  onDelete: PropTypes.func,
  onReject: PropTypes.func,
  onSubmit: PropTypes.func,
}

export default ExpenseDetailsTabFooter;
